import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { useAddPatientToQueueMutation } from '@/core/services/patient-queue'

export type PatientData = {
  _id: string
  name: string
  surname: string
  gender?: 'M' | 'F' | 'I' | 'O'
  birthday?: string
  city?: string
  secu: string
}

interface UsePatientsCardViewParams {
  disableSelectRow?: boolean
  onSelect?: (data: PatientData) => void
}

export function usePatientsCardView({ disableSelectRow = false, onSelect }: UsePatientsCardViewParams) {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const authUser = useAppSelector(selectAuthUser)
  const [addPatientToQueue] = useAddPatientToQueueMutation()
  const [selectedRow, setSelectedRow] = useState<string>()

  const selectRow = useCallback(
    (rowData: PatientData) => {
      if (disableSelectRow) return
      setSelectedRow(rowData._id)
      onSelect?.(rowData)
    },
    [disableSelectRow, onSelect]
  )

  const addToQueue = useCallback(
    async (patientId: string) => {
      try {
        if (!authUser?._id) return
        await addPatientToQueue({ staffId: authUser._id, patient: patientId }).unwrap()
        enqueueSnackbar(t('common:notification.success'), { variant: 'success' })
      } catch (e) {
        enqueueSnackbar(t('common:notification.error'), { variant: 'error' })
      }
    },
    [addPatientToQueue, authUser?._id, enqueueSnackbar, t]
  )

  return {
    selectedRow,
    selectRow,
    addToQueue,
  }
}
